'use client';

import { useId } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import {
  type AllIconProps,
  fortuneIconMap,
  type IconMap,
  type IconName,
  lucideIconMap,
} from '@/app_components/icons/constants';
import { cn } from '@/app_lib/utils/utils';

type Sizes = 'sm' | 'md' | 'lg' | 'xl' | 'custom';

// NOTE: Lucide does not properly support absoluteStrokeWidths along with tailwind sizing
// so we need to manually set the strokeWidth based on the size prop we pass in
const strokeWidths: { [Key in Sizes]: { size?: number; strokeWidth?: number } } = {
  custom: {},
  lg: {
    size: 32,
    strokeWidth: 2,
  },
  md: {
    size: 24,
    strokeWidth: 1.5,
  },
  sm: {
    size: 16,
    strokeWidth: 1,
  },
  xl: {
    size: 48,
    strokeWidth: 3,
  },
};

const icons = tv({
  base: '',
  variants: {
    mode: {
      both: 'fill-current stroke-current text-current',
      custom: '', // custom colors should be passed via className
      fill: 'fill-current stroke-none text-current',
      stroke: 'stroke-current text-current',
    },
  },
});

type IconVariants = VariantProps<typeof icons>;

const iconMap: IconMap = { ...lucideIconMap, ...fortuneIconMap };

interface IconProps extends Omit<AllIconProps, 'size'> {
  icon: IconName;
  className?: string;
  size?: Sizes;
}

function isLucideIcon(icon: IconName): icon is keyof typeof lucideIconMap {
  return Object.keys(lucideIconMap).includes(icon);
}

export const Icon = ({
  icon,
  mode = 'stroke',
  className,
  size = 'md',
  height,
  width,
  strokeWidth: strokeWidthProp,
  'aria-label': ariaLabel,
  ...rest
}: IconProps & IconVariants) => {
  const id = useId();

  const IconComponent = iconMap[icon];
  if (!IconComponent) {
    return null;
  }
  IconComponent.displayName = `CustomIcon-${icon}`;

  const iconClasses = icons({ mode });

  const strokeWidth = strokeWidths[size].strokeWidth;
  const sizeValue = strokeWidths[size].size;

  return (
    <IconComponent
      {...(isLucideIcon(icon) && { absoluteStrokeWidth: true })}
      aria-label={ariaLabel || icon}
      className={cn('shrink-0 self-center custom-icon', iconClasses, className)}
      // Custom should'nt use our custom height/width props
      {...(!(size === 'custom' && !height) && { height: height || sizeValue })}
      id={`icon-${icon}-${id}`} // Ensure unique IDs for accessibility
      strokeWidth={strokeWidthProp || strokeWidth}
      {...(!(size === 'custom' && !width) && { width: width || sizeValue })}
      {...rest}
    />
  );
};
